import React, { useState, useEffect, useRef, useCallback } from 'react'
import { useHistory, useParams } from 'react-router-dom'
import ElementsDataService from '../../api/entitiesDataServices/ElementsDataService.js'
import AuthenticationService from '../general/AuthenticationService.js'

/* ───────────────────────── helpers ───────────────────────── */

function saveFile(formData) {
    const config = { headers: { 'Content-Type': 'multipart/form-data' } }
    return ElementsDataService.createElement('cfdis/file', formData, config)
}

function formatFileSize(bytes) {
    if (bytes === 0) return '0 B'
    const k = 1024
    const sizes = ['B', 'KB', 'MB', 'GB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i]
}

/* ───────────────────────── design tokens ────────────────────── */

const t = {
    bg: '#f1f5f9',
    card: '#ffffff',
    border: '#e8edf5',
    blue: '#3b82f6',
    blueHover: '#2563eb',
    bluePale: '#eff6ff',
    navy: '#0f172a',
    textPrimary: '#1e293b',
    textSecondary: '#64748b',
    textMuted: '#94a3b8',
    success: '#10b981',
    successBg: '#ecfdf5',
    successBorder: '#a7f3d0',
    danger: '#ef4444',
    dangerBg: '#fef2f2',
    dangerBorder: '#fecaca',
    warningBg: '#fffbeb',
    warningBorder: '#fde68a',
    radius: 16,
    radiusSm: 10,
    shadow: '0 1px 3px rgba(15,23,42,.06), 0 1px 2px rgba(15,23,42,.04)',
    shadowMd: '0 4px 12px rgba(15,23,42,.08)',
    font: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
}

/* ───────────────────────── Section ──────────────────────────── */

function Section({ title, subtitle, icon, children, style }) {
    return (
        <div
            style={{
                background: t.card,
                border: `1px solid ${t.border}`,
                borderRadius: t.radius,
                boxShadow: t.shadow,
                ...style,
            }}
        >
            {(title || subtitle) && (
                <div style={{ padding: '20px 24px', borderBottom: `1px solid ${t.border}` }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        {icon && (
                            <span
                                style={{
                                    width: 36,
                                    height: 36,
                                    borderRadius: 10,
                                    background: `${t.blue}12`,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    fontSize: 17,
                                    flexShrink: 0,
                                }}
                            >
                                {icon}
                            </span>
                        )}
                        <div>
                            {title && (
                                <h2
                                    style={{
                                        margin: 0,
                                        fontSize: 16,
                                        fontWeight: 650,
                                        color: t.textPrimary,
                                        letterSpacing: '-0.01em',
                                    }}
                                >
                                    {title}
                                </h2>
                            )}
                            {subtitle && (
                                <p style={{ margin: '2px 0 0', fontSize: 13, color: t.textSecondary }}>
                                    {subtitle}
                                </p>
                            )}
                        </div>
                    </div>
                </div>
            )}
            <div style={{ padding: '20px 24px' }}>{children}</div>
        </div>
    )
}

/* ───────────────────────── FileItem ─────────────────────────── */

const STATUS_ICONS = {
    pending: '○',
    uploading: '◌',
    success: '✓',
    error: '✕',
}

const STATUS_COLORS = {
    pending: t.textMuted,
    uploading: t.blue,
    success: t.success,
    error: t.danger,
}

function FileItem({ file, status }) {
    const ext = file.name.split('.').pop().toUpperCase()

    return (
        <div
            style={{
                display: 'flex',
                alignItems: 'center',
                gap: 12,
                padding: '12px 14px',
                borderRadius: t.radiusSm,
                border: `1px solid ${
                    status === 'error'
                        ? t.dangerBorder
                        : status === 'success'
                        ? t.successBorder
                        : t.border
                }`,
                background:
                    status === 'error'
                        ? t.dangerBg
                        : status === 'success'
                        ? t.successBg
                        : t.card,
                transition: 'all .25s ease',
            }}
        >
            {/* ext badge */}
            <div
                style={{
                    width: 38,
                    height: 38,
                    borderRadius: 9,
                    background:
                        status === 'error' ? '#fef2f2' : status === 'success' ? '#ecfdf5' : '#f1f5f9',
                    border: `1px solid ${
                        status === 'error'
                            ? t.dangerBorder
                            : status === 'success'
                            ? t.successBorder
                            : t.border
                    }`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: 10,
                    fontWeight: 700,
                    color: STATUS_COLORS[status] || t.textMuted,
                    letterSpacing: '0.02em',
                    flexShrink: 0,
                }}
            >
                {ext.length <= 4 ? ext : '📄'}
            </div>

            {/* name + size */}
            <div style={{ flex: 1, minWidth: 0 }}>
                <div
                    style={{
                        fontSize: 13.5,
                        fontWeight: 550,
                        color: t.textPrimary,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                    }}
                >
                    {file.name}
                </div>
                <div style={{ fontSize: 12, color: t.textMuted, marginTop: 1 }}>
                    {formatFileSize(file.size)}
                </div>
            </div>

            {/* status icon */}
            <span
                style={{
                    fontSize: 16,
                    color: STATUS_COLORS[status] || t.textMuted,
                    fontWeight: 600,
                    flexShrink: 0,
                    transition: 'color .2s',
                }}
            >
                {status === 'uploading' ? (
                    <span className="zenyth-file-spin">{STATUS_ICONS[status]}</span>
                ) : (
                    STATUS_ICONS[status]
                )}
            </span>
        </div>
    )
}

/* ───────────────────────── ProgressBar ──────────────────────── */

function ProgressBar({ current, total }) {
    if (total === 0) return null
    const pct = Math.round((current / total) * 100)
    return (
        <div style={{ marginBottom: 18 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                <span style={{ fontSize: 12.5, fontWeight: 600, color: t.textPrimary }}>
                    Progreso
                </span>
                <span style={{ fontSize: 12.5, fontWeight: 600, color: t.blue }}>
                    {current} / {total} — {pct}%
                </span>
            </div>
            <div
                style={{
                    height: 6,
                    borderRadius: 3,
                    background: '#e8edf5',
                    overflow: 'hidden',
                }}
            >
                <div
                    style={{
                        height: '100%',
                        width: `${pct}%`,
                        borderRadius: 3,
                        background: `linear-gradient(90deg, ${t.blue}, ${t.blueHover})`,
                        transition: 'width .4s ease',
                    }}
                />
            </div>
        </div>
    )
}

/* ═══════════════════════ MAIN COMPONENT ═══════════════════════ */

function CfdiFormularioComponent() {
    const history = useHistory()
    const { id } = useParams()

    const [files, setFiles] = useState([])
    const [fileStatuses, setFileStatuses] = useState({})
    const [uploading, setUploading] = useState(false)
    const [processed, setProcessed] = useState(0)
    const [dragOver, setDragOver] = useState(false)
    const [summary, setSummary] = useState(null)

    const fileInputRef = useRef(null)

    /* ── auth guard ── */
    useEffect(() => {
        if (!AuthenticationService.haveAuthorization('WRITE_CFDIS')) {
            alert('Usuario no autorizado')
            history.push('/login')
        }
    }, []) // eslint-disable-line react-hooks/exhaustive-deps

    /* ── file selection ── */
    const addFiles = useCallback(
        (newFiles) => {
            const list = Array.from(newFiles)
            setFiles((prev) => {
                const updated = [...prev, ...list]
                /* mark new ones as pending */
                setFileStatuses((prevSt) => {
                    const next = { ...prevSt }
                    list.forEach((_, i) => {
                        next[prev.length + i] = 'pending'
                    })
                    return next
                })
                return updated
            })
            setSummary(null)
        },
        []
    )

    const removeFile = (idx) => {
        setFiles((prev) => prev.filter((_, i) => i !== idx))
        setFileStatuses((prev) => {
            const next = {}
            let j = 0
            Object.keys(prev).forEach((k) => {
                if (Number(k) !== idx) {
                    next[j] = prev[k]
                    j++
                }
            })
            return next
        })
    }

    const clearAll = () => {
        setFiles([])
        setFileStatuses({})
        setProcessed(0)
        setSummary(null)
        if (fileInputRef.current) fileInputRef.current.value = ''
    }

    /* ── drag & drop ── */
    const handleDragOver = (e) => {
        e.preventDefault()
        setDragOver(true)
    }
    const handleDragLeave = () => setDragOver(false)
    const handleDrop = (e) => {
        e.preventDefault()
        setDragOver(false)
        if (e.dataTransfer.files.length) addFiles(e.dataTransfer.files)
    }

    /* ── upload ── */
    const handleSubmit = async () => {
        if (files.length === 0) return

        setUploading(true)
        setProcessed(0)
        setSummary(null)

        let successes = 0
        const failures = []

        for (let i = 0; i < files.length; i++) {
            /* skip already-uploaded files */
            if (fileStatuses[i] === 'success') {
                setProcessed((p) => p + 1)
                successes++
                continue
            }

            setFileStatuses((prev) => ({ ...prev, [i]: 'uploading' }))

            try {
                const formData = new FormData()
                formData.append('metadataFiles', files[i])

                const response = await saveFile(formData)

                if (response && response.data.message === 'Created') {
                    successes++
                    setFileStatuses((prev) => ({ ...prev, [i]: 'success' }))
                } else {
                    failures.push(files[i].name)
                    setFileStatuses((prev) => ({ ...prev, [i]: 'error' }))
                }
            } catch (err) {
                console.error('Upload error:', err)
                failures.push(files[i].name)
                setFileStatuses((prev) => ({ ...prev, [i]: 'error' }))
            }

            setProcessed(i + 1)
        }

        setUploading(false)
        setSummary({ successes, failures })
    }

    /* ── computed ── */
    const pendingCount = Object.values(fileStatuses).filter((s) => s === 'pending' || s === 'error').length
    const canSubmit = files.length > 0 && !uploading && pendingCount > 0

    /* ────────────────── RENDER ────────────────── */
    return (
        <div
            style={{
                minHeight: '100vh',
                background: t.bg,
                fontFamily: t.font,
                padding: '32px 32px 64px',
            }}
        >
            <style>{`
                @keyframes zenyth-file-spin-kf {
                    to { transform: rotate(360deg); }
                }
                .zenyth-file-spin {
                    display: inline-block;
                    animation: zenyth-file-spin-kf .8s linear infinite;
                }
            `}</style>

            <div style={{ maxWidth: 680, margin: '0 auto' }}>

                {/* Header */}
                <div style={{ marginBottom: 28 }}>
                    <button
                        onClick={() => history.goBack()}
                        style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 4,
                            background: 'none',
                            border: 'none',
                            color: t.textSecondary,
                            fontSize: 13,
                            fontWeight: 550,
                            fontFamily: t.font,
                            cursor: 'pointer',
                            padding: 0,
                            marginBottom: 12,
                        }}
                    >
                        ← Regresar
                    </button>
                    <h1
                        style={{
                            margin: 0,
                            fontSize: 26,
                            fontWeight: 700,
                            color: t.navy,
                            letterSpacing: '-0.02em',
                        }}
                    >
                        Cargar Metadata CFDI
                    </h1>
                    <p style={{ margin: '4px 0 0', fontSize: 14, color: t.textSecondary }}>
                        Sube uno o varios archivos de metadata para procesarlos
                    </p>
                </div>

                {/* ── Drop zone ── */}
                <Section
                    title="Archivos"
                    subtitle="Arrastra archivos o haz clic para seleccionar"
                    icon="📎"
                >
                    <div
                        onClick={() => fileInputRef.current?.click()}
                        onDragOver={handleDragOver}
                        onDragLeave={handleDragLeave}
                        onDrop={handleDrop}
                        style={{
                            border: `2px dashed ${dragOver ? t.blue : t.border}`,
                            borderRadius: t.radiusSm,
                            padding: '36px 24px',
                            textAlign: 'center',
                            cursor: 'pointer',
                            background: dragOver ? t.bluePale : '#fafbfd',
                            transition: 'all .2s ease',
                            marginBottom: files.length > 0 ? 16 : 0,
                        }}
                    >
                        <div
                            style={{
                                fontSize: 32,
                                marginBottom: 8,
                                opacity: dragOver ? 1 : 0.5,
                                transition: 'opacity .2s',
                            }}
                        >
                            📂
                        </div>
                        <p
                            style={{
                                margin: 0,
                                fontSize: 14,
                                fontWeight: 600,
                                color: dragOver ? t.blue : t.textPrimary,
                            }}
                        >
                            {dragOver ? 'Suelta los archivos aquí' : 'Clic para seleccionar archivos'}
                        </p>
                        <p style={{ margin: '4px 0 0', fontSize: 12.5, color: t.textMuted }}>
                            Puedes seleccionar múltiples archivos a la vez
                        </p>
                        <input
                            ref={fileInputRef}
                            type="file"
                            multiple
                            onChange={(e) => {
                                if (e.target.files.length) addFiles(e.target.files)
                            }}
                            style={{ display: 'none' }}
                        />
                    </div>

                    {/* File list */}
                    {files.length > 0 && (
                        <>
                            <div
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    marginBottom: 10,
                                }}
                            >
                                <span style={{ fontSize: 13, fontWeight: 600, color: t.textSecondary }}>
                                    {files.length} archivo{files.length !== 1 ? 's' : ''} seleccionado
                                    {files.length !== 1 ? 's' : ''}
                                </span>
                                {!uploading && (
                                    <button
                                        onClick={clearAll}
                                        style={{
                                            background: 'none',
                                            border: 'none',
                                            color: t.danger,
                                            fontSize: 12.5,
                                            fontWeight: 550,
                                            fontFamily: t.font,
                                            cursor: 'pointer',
                                            padding: '2px 0',
                                        }}
                                    >
                                        Quitar todos
                                    </button>
                                )}
                            </div>

                            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                                {files.map((file, idx) => (
                                    <div key={file.name + '-' + idx} style={{ position: 'relative' }}>
                                        <FileItem file={file} status={fileStatuses[idx] || 'pending'} />
                                        {!uploading && fileStatuses[idx] !== 'success' && (
                                            <button
                                                onClick={(e) => {
                                                    e.stopPropagation()
                                                    removeFile(idx)
                                                }}
                                                style={{
                                                    position: 'absolute',
                                                    top: 8,
                                                    right: 8,
                                                    width: 22,
                                                    height: 22,
                                                    borderRadius: 6,
                                                    border: 'none',
                                                    background: '#f1f5f9',
                                                    color: t.textMuted,
                                                    cursor: 'pointer',
                                                    fontSize: 11,
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    fontWeight: 700,
                                                    opacity: 0.7,
                                                    transition: 'opacity .15s',
                                                }}
                                                onMouseEnter={(e) => (e.currentTarget.style.opacity = '1')}
                                                onMouseLeave={(e) => (e.currentTarget.style.opacity = '0.7')}
                                            >
                                                ✕
                                            </button>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </>
                    )}
                </Section>

                {/* ── Progress ── */}
                {uploading && (
                    <div style={{ marginTop: 16 }}>
                        <ProgressBar current={processed} total={files.length} />
                    </div>
                )}

                {/* ── Summary ── */}
                {summary && (
                    <div
                        style={{
                            marginTop: 16,
                            background: summary.failures.length === 0 ? t.successBg : t.warningBg,
                            border: `1px solid ${
                                summary.failures.length === 0 ? t.successBorder : t.warningBorder
                            }`,
                            borderRadius: t.radiusSm,
                            padding: '16px 20px',
                        }}
                    >
                        <div
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 8,
                                marginBottom: summary.failures.length > 0 ? 8 : 0,
                            }}
                        >
                            <span style={{ fontSize: 16 }}>
                                {summary.failures.length === 0 ? '✓' : '⚠'}
                            </span>
                            <span style={{ fontSize: 14, fontWeight: 600, color: t.textPrimary }}>
                                {summary.failures.length === 0
                                    ? `${summary.successes} archivo${summary.successes !== 1 ? 's' : ''} cargado${summary.successes !== 1 ? 's' : ''} exitosamente`
                                    : `${summary.successes} de ${files.length} archivos cargados`}
                            </span>
                        </div>
                        {summary.failures.length > 0 && (
                            <div
                                style={{
                                    fontSize: 13,
                                    color: t.danger,
                                    lineHeight: 1.6,
                                    paddingLeft: 24,
                                }}
                            >
                                Error en: {summary.failures.join(', ')}
                            </div>
                        )}
                    </div>
                )}

                {/* ── Submit ── */}
                <div style={{ marginTop: 20, display: 'flex', gap: 10 }}>
                    <button
                        onClick={handleSubmit}
                        disabled={!canSubmit}
                        style={{
                            flex: 1,
                            padding: '13px 24px',
                            background: canSubmit ? t.blue : '#cbd5e1',
                            border: 'none',
                            borderRadius: t.radiusSm,
                            color: '#fff',
                            fontSize: 14.5,
                            fontWeight: 600,
                            fontFamily: t.font,
                            cursor: canSubmit ? 'pointer' : 'not-allowed',
                            transition: 'all .15s',
                            boxShadow: canSubmit ? '0 4px 12px rgba(59,130,246,.3)' : 'none',
                            letterSpacing: '-0.01em',
                        }}
                        onMouseEnter={(e) => {
                            if (canSubmit) e.currentTarget.style.background = t.blueHover
                        }}
                        onMouseLeave={(e) => {
                            if (canSubmit) e.currentTarget.style.background = t.blue
                        }}
                    >
                        {uploading
                            ? `Procesando ${processed} de ${files.length}...`
                            : `Subir${files.length > 0 ? ' ' + files.length + ' archivo' + (files.length !== 1 ? 's' : '') : ''}`}
                    </button>

                    {summary && summary.failures.length === 0 && (
                        <button
                            onClick={clearAll}
                            style={{
                                padding: '13px 20px',
                                background: t.card,
                                border: `1px solid ${t.border}`,
                                borderRadius: t.radiusSm,
                                color: t.textSecondary,
                                fontSize: 14,
                                fontWeight: 550,
                                fontFamily: t.font,
                                cursor: 'pointer',
                                transition: 'all .15s',
                            }}
                        >
                            Cargar más
                        </button>
                    )}
                </div>
            </div>
        </div>
    )
}

export default CfdiFormularioComponent
